import axios, { type AxiosError, type InternalAxiosRequestConfig, isAxiosError } from 'axios';

import { getAccessToken, getRefreshToken, redirectToExternalAuth, removeTokens } from '../auth';
import { env } from '../env';
import { type OneOfAppIdentities, appIdentity as appIdentityConsts } from '../consts/appIdentity';
import { randomUUID } from '../randomId';
import { PERM_TOKEN_ROUTE, REFRESH_TOKEN_ROUTE, sendTokenRefreshRequest } from './login';
import { grpcResponceCodes, httpResponseCodes } from './codeConsts';
import errorLogger from '@/services/logger/ErrorLogger';
import { createErrorWithCause } from '@/utils/createErrorWithCause';
import { getLocaleFromCookie } from '@/components/new-order/lang/LocalizationProvider';

export const appIdentityHeaderKey = 'App-Identity';
export const traceIDHeaderKey = 'Trace-Id';
const defaulAppIdentify = appIdentityConsts.orderDefault;

interface DefaulClearAPIError {
  code: number;
  message: string;
}

const clearApiClient = axios.create({
  baseURL: env.apiURL,
  headers: {
    [appIdentityHeaderKey]: defaulAppIdentify,
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

const injectHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  config.headers.Authorization = getAccessToken();
  config.headers[traceIDHeaderKey] = randomUUID();
  config.headers['User-Locale'] = getLocaleFromCookie();

  return config;
};

const unrefreshableRoutes = [REFRESH_TOKEN_ROUTE, PERM_TOKEN_ROUTE] as const;
const isUnrefreshableRoute = (url: string): boolean => (unrefreshableRoutes as readonly string[]).includes(url);

let refreshPromise: Promise<void> | null = null;

clearApiClient.interceptors.request.use((config) => {
  // preventing refresh promise being chained to refresh promise, thus never resolving
  if (config?.url && isUnrefreshableRoute(config.url)) {
    return config;
  }

  if (refreshPromise) {
    return refreshPromise.then(() => injectHeaders(config));
  }

  return injectHeaders(config);
});

clearApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAxiosError<DefaulClearAPIError>(error)) {
      if (
        error?.response?.status === httpResponseCodes.UNAUTHORIZED &&
        error.response.data.code === grpcResponceCodes.UNAUTHENTICATED &&
        error.config?.url &&
        !isUnrefreshableRoute(error.config.url)
      ) {
        const refreshToken = getRefreshToken();

        if (refreshToken) {
          if (!refreshPromise) {
            refreshPromise = sendTokenRefreshRequest(refreshToken).then(
              () => {
                refreshPromise = null;
              },
              (refreshError: unknown) => {
                // refreash failed, redirect to the pit
                removeTokens();
                redirectToExternalAuth();
                throw refreshError;
              },
            );
          }

          return refreshPromise.then(() => {
            if (error.config) {
              console.log('Sending retry with config: ', injectHeaders(error.config));

              return clearApiClient(injectHeaders(error.config));
            }

            throw createErrorWithCause('No config for retry', error);
          });
        }
        // no refresh token, but go unauthenticated, redirect to the pit
        redirectToExternalAuth();
      }

      errorLogger.logApiError(error);
      throw error;
    } else {
      if (isAxiosError(error)) {
        errorLogger.logCrash(error, { message: error.toJSON() });
      } else {
        errorLogger.logCrash(error as Error);
      }
      removeTokens();
      redirectToExternalAuth();
      throw error;
    }
  },
);

/**
 * Sets `App-Identity` header for all outgoing API requests
 */
const setInstanceAppIdentity = (appIdentity: OneOfAppIdentities) => {
  clearApiClient.defaults.headers[appIdentityHeaderKey] = appIdentity;
};

const isApiError = (error: unknown): error is AxiosError<DefaulClearAPIError> =>
  isAxiosError<DefaulClearAPIError>(error);

export { clearApiClient, setInstanceAppIdentity, isApiError };
